/*
 * Praise — Prayer stream (Twitch-style live chat)
 * <prayer-stream> shows moderated, opted-in prayers in a scrollable panel pinned to the bottom:
 *   - New prayers append at the bottom; if you're at the bottom it follows them.
 *   - Scroll up to read and it holds still — a "↓ N new prayers" button appears; click it (or
 *     scroll back to the bottom) to jump to the newest.
 *   - Each person gets a consistent avatar color so new names are easy to tell apart.
 * Form submit also emails the full request to the team (/notify) and, when opted in, adds
 * first-name + text to the public stream (/submit) — without blocking Shopify's contact submit.
 *
 * All prayer text is inserted with textContent (never innerHTML).
 */
(function () {
  'use strict';

  var BOTTOM_THRESHOLD = 48; // px from the bottom that still counts as "following"
  var AVATAR_COLORS = ['#24d2ff', '#ffcc37', '#4ee0a0', '#ff8fab', '#b89cff']; // rotate through these by row

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function stripTrailingSlash(s) { return (s || '').replace(/\/+$/, ''); }
  function maxTsOf(list) { var m = 0; (list || []).forEach(function (i) { if (i.ts > m) m = i.ts; }); return m; }

  function relTime(ts) {
    if (!ts) return '';
    var s = Math.max(0, Math.floor((Date.now() - ts) / 1000));
    if (s < 45) return 'just now';
    if (s < 90) return '1 min ago';
    if (s < 3600) return Math.floor(s / 60) + ' min ago';
    if (s < 7200) return '1 hr ago';
    if (s < 86400) return Math.floor(s / 3600) + ' hr ago';
    return Math.floor(s / 86400) + ' d ago';
  }

  // ---- one chat row: colored avatar + name + time + text (safe: textContent only) ----
  function makeMessage(item, index) {
    var displayName = item && item.n ? String(item.n) : 'Someone';

    var row = document.createElement('div');
    row.className = 'prayer-msg';

    var avatar = document.createElement('div');
    avatar.className = 'prayer-msg__avatar font-heading';
    avatar.style.background = AVATAR_COLORS[index % AVATAR_COLORS.length];
    avatar.textContent = (displayName.charAt(0) || '?').toUpperCase();

    var body = document.createElement('div');
    body.className = 'prayer-msg__body';

    var head = document.createElement('div');
    head.className = 'prayer-msg__head';

    var name = document.createElement('span');
    name.className = 'prayer-msg__name font-heading';
    name.textContent = displayName;

    var time = document.createElement('span');
    time.className = 'prayer-msg__time';
    time.textContent = item ? relTime(item.ts) : '';

    head.appendChild(name);
    head.appendChild(time);

    var text = document.createElement('p');
    text.className = 'prayer-msg__text font-secondary';
    text.textContent = item && item.t ? String(item.t) : '';

    body.appendChild(head);
    body.appendChild(text);
    row.appendChild(avatar);
    row.appendChild(body);
    return row;
  }

  // ---- <prayer-stream> element ----
  if (!customElements.get('prayer-stream')) {
    customElements.define('prayer-stream', class extends HTMLElement {
      connectedCallback() {
        if (this._init) return;
        this._init = true;
        var self = this;

        this.endpoint = stripTrailingSlash(this.dataset.endpoint);
        this.poll = parseInt(this.dataset.poll, 10) || 3000;
        this.max = parseInt(this.dataset.max, 10) || 40;
        this.viewport = this.querySelector('[data-prayer-stream-viewport]');
        this.track = this.querySelector('[data-prayer-stream-track]');
        this.emptyEl = this.querySelector('[data-prayer-stream-empty]');
        this.pill = this.querySelector('[data-prayer-stream-pill]');
        this.lastSig = null;
        this.items = [];
        this.pending = [];
        this.displayedMaxTs = 0;

        if (!this.endpoint || !this.track || !this.viewport) { this.showEmpty(true); return; }

        // Scrolled back to the bottom? catch up + clear the button.
        this.viewport.addEventListener('scroll', function () {
          if (self.isAtBottom()) {
            if (maxTsOf(self.pending) > self.displayedMaxTs) self.build();
            else self.hidePill();
          }
        }, { passive: true });

        if (this.pill) this.pill.addEventListener('click', function () {
          self.build();
          self.scrollToBottom(true);
        });

        this.load();
        this.timer = setInterval(this.load.bind(this), this.poll);
        this._onResize = debounce(function () { if (this.isAtBottom()) this.scrollToBottom(); }.bind(this), 150);
        window.addEventListener('resize', this._onResize);
      }

      disconnectedCallback() {
        clearInterval(this.timer);
        if (this._onResize) window.removeEventListener('resize', this._onResize);
      }

      isAtBottom() {
        var vp = this.viewport;
        return vp.scrollHeight - vp.scrollTop - vp.clientHeight <= BOTTOM_THRESHOLD;
      }

      scrollToBottom(smooth) {
        var vp = this.viewport;
        if (smooth && vp.scrollTo) vp.scrollTo({ top: vp.scrollHeight, behavior: 'smooth' });
        else vp.scrollTop = vp.scrollHeight;
      }

      load() {
        var self = this;
        fetch(this.endpoint + '/prayers?max=' + this.max, { headers: { Accept: 'application/json' } })
          .then(function (res) { if (!res.ok) throw new Error('bad'); return res.json(); })
          .then(function (data) {
            var items = Array.isArray(data) ? data : (data && data.prayers) || [];
            self.items = items;
            self.render(items);
          })
          .catch(function () { if (!self.track.children.length) self.showEmpty(true); });
      }

      render(items) {
        var sig = items.length + '|' + maxTsOf(items);
        if (sig === this.lastSig) return;
        this.lastSig = sig;
        this.pending = items;

        if (!items.length) {
          this.track.replaceChildren();
          this.showEmpty(true);
          this.hidePill();
          return;
        }

        // First paint, or following at the bottom -> render + stick. Otherwise notify only.
        if (!this.displayedMaxTs || this.isAtBottom()) {
          this.build();
        } else if (maxTsOf(items) > this.displayedMaxTs) {
          this.showPill(this.countNew(items));
        }
      }

      build() {
        if (!this.pending.length) return;
        this.showEmpty(false);
        this.hidePill();

        // Newest at the bottom; cap to max so the DOM stays bounded (pruning).
        var items = this.pending.slice(0, this.max);
        this.displayedMaxTs = maxTsOf(items);
        var ordered = items.slice().reverse(); // oldest -> newest

        var track = this.track, vp = this.viewport;
        track.replaceChildren();
        var idx = 0;
        var renderPass = function () {
          var frag = document.createDocumentFragment();
          ordered.forEach(function (it) { frag.appendChild(makeMessage(it, idx++)); });
          track.appendChild(frag);
        };
        renderPass();
        // Not enough prayers to fill the panel yet? Duplicate the set so it looks full.
        var guard = 0;
        while (track.scrollHeight < vp.clientHeight && guard < 40) { renderPass(); guard++; }

        this.scrollToBottom();
      }

      countNew(items) {
        var self = this, n = 0;
        items.forEach(function (i) { if (i.ts > self.displayedMaxTs) n++; });
        return n;
      }

      showPill(n) {
        if (!this.pill || n <= 0) return;
        this.pill.textContent = '↓ ' + n + ' new prayer' + (n > 1 ? 's' : '');
        this.pill.hidden = false;
      }

      hidePill() { if (this.pill) this.pill.hidden = true; }
      showEmpty(v) { if (this.emptyEl) this.emptyEl.hidden = !v; }
    });
  }

  // ---- swap the form fields for an in-place "thank you" once we've delivered ----
  function showPrayerSuccess(box) {
    var form = box.querySelector('form');
    if (!form) return;
    var msg = box.dataset.successMsg || 'Thank you for trusting us with this. Our team will be praying for you.';
    var wallLabel = box.dataset.wallLabel || '';
    var wrap = document.createElement('div');
    var p = document.createElement('p');
    p.className = 'form-success';
    p.textContent = msg;
    wrap.appendChild(p);
    if (wallLabel) {
      var a = document.createElement('a');
      a.href = '#prayer-wall';
      a.className = 'push-btn mt-5 inline-block';
      var s = document.createElement('span');
      s.className = 'push-btn__surface';
      s.textContent = wallLabel + ' ↓';
      a.appendChild(s);
      wrap.appendChild(a);
    }
    form.replaceChildren(wrap);
    try { box.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) {}
  }

  // ---- form submit -> deliver to the prayer inbox via the Worker (Resend), NOT Shopify's
  //      contact inbox. We take over the submit so nothing is emailed to the store contact
  //      address; the native contact form only fires as a fallback when JavaScript is off. ----
  function attachForms(root) {
    (root || document).querySelectorAll('[data-prayer-form]').forEach(function (box) {
      var form = box.querySelector('form');
      if (!form || form._prayerBound) return;
      form._prayerBound = true;

      form.addEventListener('submit', function (e) {
        var endpoint = stripTrailingSlash(box.dataset.streamEndpoint);
        if (!endpoint) return; // no Worker configured -> allow the native submit as a fallback

        var hp = box.querySelector('[data-prayer-honeypot]');
        if (hp && hp.value) { e.preventDefault(); return; } // bot -> drop silently, never submit

        try {
          var val = function (sel) { var el = box.querySelector(sel) || form.querySelector(sel); return el ? (el.value || '').trim() : ''; };
          var checked = function (sel) { var el = box.querySelector(sel) || form.querySelector(sel); return !!(el && el.checked); };

          var firstName = val('[data-prayer-firstname]');
          var lastName = val('[name="contact[Last name]"]');
          var email = val('[name="contact[email]"]');
          var prayerText = val('[data-prayer-topic]');
          var replyOk = checked('[name="contact[Okay to email me back]"]');
          var streamOk = checked('[data-prayer-stream-consent]');
          var videoOk = checked('[name="contact[Okay for Bryce to read on video or livestream]"]');
          if (!firstName || !prayerText) return;

          // We're delivering through the Worker, so stop Shopify's native contact submit
          // (that's the one that used to email the store contact address).
          e.preventDefault();
          if (form._prayerSent) return; // already delivered from this page load

          var post = function (path, obj) {
            var payload = JSON.stringify(obj);
            var url = endpoint + path;
            var blob = new Blob([payload], { type: 'text/plain' });
            if (!(navigator.sendBeacon && navigator.sendBeacon(url, blob))) {
              fetch(url, { method: 'POST', body: payload, headers: { 'Content-Type': 'text/plain' }, keepalive: true, mode: 'cors' }).catch(function () {});
            }
          };

          post('/notify', { firstName: firstName, lastName: lastName, email: email, prayerText: prayerText, replyOk: replyOk, streamOk: streamOk, videoOk: videoOk });
          if (box.dataset.streamEnabled === 'true' && streamOk) {
            post('/submit', { firstName: firstName, prayerText: prayerText });
          }

          form._prayerSent = true;
          showPrayerSuccess(box);
        } catch (err) {
          // Only if something broke before we delivered: fall back to the native submit
          // so a prayer request is never silently lost.
          if (!form._prayerSent) { try { form.submit(); } catch (e2) {} }
        }
      }, true);
    });
  }

  function init() { attachForms(); }
  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', function (e) { attachForms(e.target || document); });
})();
